import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IAudition } from '../../../../types/global/helper';
import { updateAudition } from '../../../../redux/audition/actions';
import { useGoogleMapsLoader } from '../../../../hook/useMapLoader';
import { AuditionDetailsEditFormBody } from './AuditionDetailsEditFormBody';
import { buildFullUpdatePayload, seedAllEditedValues } from './auditionDetailsEditState';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';

export interface AuditionDetailsEditModalProps {
    open: boolean;
    onClose: () => void;
    audition: IAudition | null;
    /** `brand` matches brand campaign / audition detail surfaces and controls. */
    visualVariant?: 'dashboard' | 'brand';
    /** Optional extra dialog paper `sx` (merged after variant defaults). */
    paperSx?: SxProps<Theme>;
}

const roundedBtn = {
    borderRadius: 999,
    px: 3,
    py: 1.1,
    textTransform: 'none' as const,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 13,
    minWidth: 120,
};

export const AuditionDetailsEditModal: React.FC<AuditionDetailsEditModalProps> = ({
    open,
    onClose,
    audition,
    visualVariant = 'dashboard',
    paperSx,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { isLoaded: isMapsLoaded } = useGoogleMapsLoader();
    const [editedValues, setEditedValues] = useState<Record<string, any>>({});
    const [baseline, setBaseline] = useState<Record<string, any> | null>(null);
    const fs = theme[themeStyle as keyof typeof theme];
    const isBrand = visualVariant === 'brand';

    const hasUnsavedChanges = useMemo(
        () => baseline != null && !isEqual(editedValues, baseline),
        [editedValues, baseline],
    );

    const auditionRef = useRef(audition);
    auditionRef.current = audition;

    useEffect(() => {
        if (!open) {
            setBaseline(null);
            return;
        }
        const a = auditionRef.current;
        if (!a?._id) return;
        const seed = seedAllEditedValues(a);
        setEditedValues(seed);
        setBaseline(cloneDeep(seed));
    }, [open, audition?._id]);

    const handleFieldChange = useCallback((field: string, value: any) => {
        setEditedValues(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSave = useCallback(() => {
        if (!audition?._id || !hasUnsavedChanges) return;
        const body = buildFullUpdatePayload(editedValues);
        dispatch(
            updateAudition({
                id: audition._id,
                body,
                isImageUpload: false,
            }),
        );
        onClose();
    }, [audition?._id, dispatch, editedValues, hasUnsavedChanges, onClose]);

    if (!audition) {
        return null;
    }

    const paperBrand: SxProps<Theme> = isBrand
        ? {
              backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
              color: 'rgba(255,255,255,0.92)',
              border: `1px solid ${BRAND_OUTLINE_VARIANT}55`,
              borderRadius: '16px',
              boxShadow: '0px 18px 46px rgba(0, 0, 0, 0.6)',
              overflow: 'hidden',
          }
        : {
              bgcolor: fs.pure_black,
              color: fs.primary_white,
              borderRadius: 2,
          };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            disableScrollLock
            slotProps={{
                backdrop: {
                    sx: isBrand ? { backgroundColor: 'rgba(6, 6, 6, 0.78)' } : { backgroundColor: 'rgba(0,0,0,0.5)' },
                },
            }}
            PaperProps={{
                sx: {
                    maxHeight: 'min(90vh, 880px)',
                    display: 'flex',
                    flexDirection: 'column',
                    ...paperBrand,
                    ...(paperSx || {}),
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    px: 2.5,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    flexShrink: 0,
                    ...(isBrand
                        ? {
                              backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                              borderBottom: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                          }
                        : {
                              borderBottom: `1px solid ${fs.form_black}`,
                          }),
                }}>
                <Box sx={{ width: 40, height: 40 }} />
                <Typography
                    component="span"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: '1.35rem', sm: '1.5rem' },
                        lineHeight: 1.1,
                        textAlign: 'center',
                        flex: 1,
                        color: fs.primary_white,
                    }}>
                    Edit audition details
                </Typography>
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        color: isBrand ? 'rgba(255,255,255,0.55)' : fs.tag_white,
                        '&:hover': {
                            color: isBrand ? BRAND_LUXE_PRIMARY : fs.primary_white,
                            bgcolor: isBrand ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)',
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: { xs: 2, sm: 2.5 },
                    py: 2,
                    ...(isBrand
                        ? {
                              borderColor: `${BRAND_OUTLINE_VARIANT} !important`,
                              backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                          }
                        : {
                              borderColor: fs.form_black,
                          }),
                }}>
                <AuditionDetailsEditFormBody
                    themeStyle={themeStyle}
                    values={editedValues}
                    onFieldChange={handleFieldChange}
                    isMapsLoaded={isMapsLoaded}
                    visualVariant={visualVariant}
                />
            </DialogContent>
            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2.5,
                    flexShrink: 0,
                    ...(isBrand
                        ? {
                              borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                              backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                          }
                        : {
                              borderTop: `1px solid ${fs.form_black}`,
                          }),
                }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges}
                    disableElevation
                    sx={{
                        ...roundedBtn,
                        width: '100%',
                        borderRadius: 1.5,
                        minHeight: 56,
                        ...(isBrand
                            ? {
                                  bgcolor: !hasUnsavedChanges ? '#7a7a7a' : BRAND_LUXE_PRIMARY,
                                  color: !hasUnsavedChanges ? 'rgba(255,255,255,0.85)' : '#0a1211',
                                  border: 'none',
                                  '&:hover': {
                                      bgcolor: !hasUnsavedChanges ? '#7a7a7a' : BRAND_LUXE_PRIMARY,
                                      opacity: !hasUnsavedChanges ? 1 : 0.94,
                                  },
                                  '&.Mui-disabled': {
                                      bgcolor: '#7a7a7a',
                                      color: 'rgba(255,255,255,0.55)',
                                  },
                              }
                            : {
                                  backgroundColor: !hasUnsavedChanges ? '#6b6b6b' : fs.highlight,
                                  color: !hasUnsavedChanges ? 'rgba(255,255,255,0.75)' : fs.black,
                                  '&:hover': {
                                      backgroundColor: !hasUnsavedChanges ? '#6b6b6b' : fs.highlight,
                                      opacity: !hasUnsavedChanges ? 1 : 0.92,
                                  },
                                  '&.Mui-disabled': {
                                      backgroundColor: '#6b6b6b',
                                      color: 'rgba(255,255,255,0.5)',
                                  },
                              }),
                    }}>
                    Save changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};
